import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { FormFieldType } from '../../types/form';
import { FORM_FIELD_TYPE_LABELS } from '../../types/form';

interface FormFieldTypeSelectProps {
  value: FormFieldType;
  onChange: (type: FormFieldType) => void;
}

const FIELD_TYPE_ORDER: FormFieldType[] = [
  'ShortText',
  'Paragraph',
  'MultipleChoice',
  'Checkboxes',
  'Dropdown',
  'FileUpload',
  'Rating',
  'Date',
  'Time',
];

const MENU_WIDTH = 224;
const MENU_MAX_HEIGHT = 256;
const VIEWPORT_MARGIN = 8;
const MIN_MENU_HEIGHT = 120;

const iconClassName = 'h-5 w-5 shrink-0 text-gray-600';

function FieldTypeIcon({ type }: { type: FormFieldType }) {
  switch (type) {
    case 'ShortText':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={iconClassName}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 8h10M4 16h6"
          />
        </svg>
      );
    case 'Paragraph':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={iconClassName}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 10h16M4 14h10M4 18h6"
          />
        </svg>
      );
    case 'MultipleChoice':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={iconClassName}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden
        >
          <circle cx="12" cy="12" r="8" strokeWidth={2} />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4"
          />
        </svg>
      );
    case 'Checkboxes':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={iconClassName}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden
        >
          <rect x="4" y="4" width="16" height="16" rx="2" strokeWidth={2} />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12l3 3 5-5"
          />
        </svg>
      );
    case 'Dropdown':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={iconClassName}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 9l4-4 4 4M8 15l4 4 4-4"
          />
        </svg>
      );
    case 'Date':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={iconClassName}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      );
    case 'Time':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={iconClassName}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
    case 'FileUpload':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={iconClassName}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
          />
        </svg>
      );
    case 'Rating':
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={iconClassName}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
          />
        </svg>
      );
    case 'Image':
    case 'Video':
    default:
      return null;
  }
}

export const FormFieldTypeSelect: React.FC<FormFieldTypeSelectProps> = ({
  value,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
    maxHeight: number;
  } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect)
    {
      return;
    }

    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
    const maxHeight = Math.max(
      MIN_MENU_HEIGHT,
      Math.min(MENU_MAX_HEIGHT, spaceBelow)
    );

    const left = Math.min(
      Math.max(VIEWPORT_MARGIN, rect.left),
      window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN
    );

    setCoords({
      top: rect.bottom + 4,
      left,
      maxHeight,
    });
  }, []);

  useLayoutEffect(() => {
    if (isOpen)
    {
      updatePosition();
    }
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen)
    {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      )
      {
        return;
      }

      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape')
      {
        setIsOpen(false);
      }
    };

    const handleReposition = () => {
      updatePosition();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [isOpen, updatePosition]);

  const handleSelect = (type: FormFieldType) => {
    onChange(type);
    setIsOpen(false);
  };

  return (
    <div className="relative shrink-0">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Tipo de pergunta"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
        className={`inline-flex min-w-[12.5rem] items-center gap-2 rounded-lg border bg-white px-3 py-2.5 text-sm text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          isOpen ? 'border-blue-400 ring-1 ring-blue-200' : 'border-gray-200'
        }`}
      >
        <FieldTypeIcon type={value} />
        <span className="flex-1 truncate text-left">
          {FORM_FIELD_TYPE_LABELS[value]}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen &&
        coords &&
        createPortal(
          <div
            ref={menuRef}
            role="listbox"
            aria-label="Tipos de pergunta"
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              width: MENU_WIDTH,
              maxHeight: coords.maxHeight,
            }}
            className="z-50 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            {FIELD_TYPE_ORDER.map((type) => {
              const isSelected = type === value;

              return (
                <button
                  key={type}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelect(type)}
                  className={`flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors ${
                    isSelected
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <FieldTypeIcon type={type} />
                  <span>{FORM_FIELD_TYPE_LABELS[type]}</span>
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
};
